/**
 * Smile Product Points – mirrors Smile Shopify App Extension API usage
 * for dynamic content (quick-add modal, etc.) where the app block does not re-init.
 */
window.SmileProductPoints = {
  _configPromise: null,

  getChannelKey() {
    return document.querySelector('.smile-shopify-init')?.dataset?.channelKey || null;
  },

  getHeaders(channelKey) {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
    return {
      Accept: 'application/json',
      'Smile-Channel-Key': channelKey,
      'Smile-Client': 'smile-shopify-app-extensions',
      'Content-Type': 'application/json',
      'X-Smile-Language': locale.split('-')[0] === locale ? locale : locale,
    };
  },

  fetchConfig() {
    if (this._configPromise) return this._configPromise;

    const channelKey = this.getChannelKey();
    if (!channelKey || !window.fetch) {
      return Promise.resolve(null);
    }

    const apiHost = 'platform.smile.io';
    const headers = this.getHeaders(channelKey);
    const designMode = window.Shopify?.designMode;

    this._configPromise = fetch(
      `https://${apiHost}/shopify_extensions/init/points_on_product_page?channel_key=${encodeURIComponent(channelKey)}${cacheModeQuery(designMode)}`,
      { headers }
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((programData) => {
        if (!programData?.account?.id) return null;

        return fetch(
          `https://${apiHost}/shopify_extensions/activity_rules?account_id=${programData.account.id}`,
          { headers }
        )
          .then((response) => (response.ok ? response.json() : null))
          .then((rulesPayload) => ({
            programData,
            activityRules: rulesPayload?.activity_rules || [],
          }));
      })
      .catch(() => null);

    return this._configPromise;
  },

  getExchangeRate() {
    return window.Shopify?.currency?.rate || 1;
  },

  convertCurrency(priceCents) {
    const rate = this.getExchangeRate();
    return rate ? priceCents / rate : priceCents;
  },

  isVariantDiscounted(variant) {
    return variant?.compare_at_price != null && variant.compare_at_price > variant.price;
  },

  calculateRulePoints(rule, priceInCurrencyUnits) {
    if (rule.reward_value_type === 'fixed') {
      return rule.reward?.value ?? 0;
    }

    if (rule.reward_value_type === 'variable') {
      const step = rule.step || 1;
      const multiplier = rule.multiplier || 1;
      return Math.round((priceInCurrencyUnits / step) * multiplier);
    }

    return null;
  },

  calculatePoints(variant, config) {
    if (!variant || !config) return null;

    const { programData, activityRules } = config;

    if (!programData?.points_program?.is_enabled || !programData.is_available) {
      return null;
    }

    const excludedIds = programData.excluded_shopify_product_ids || [];
    if (variant.product_id && excludedIds.includes(Number(variant.product_id))) {
      return null;
    }

    if (programData.exclude_discounted_products && this.isVariantDiscounted(variant)) {
      return null;
    }

    const orderRules = activityRules.filter(
      (rule) => rule.type === 'shopify_online_order' && rule.is_enabled
    );

    if (!orderRules.length) return null;

    const priceInCurrencyUnits = this.convertCurrency(variant.price) / 100;
    let points = null;

    orderRules.forEach((rule) => {
      const rulePoints = this.calculateRulePoints(rule, priceInCurrencyUnits);
      if (rulePoints === null) return;
      if (points === null || rulePoints < points) {
        points = rulePoints;
      }
    });

    return points;
  },

  getVariantFromContainer(container) {
    const productInfo = container.closest?.('product-info') || container.querySelector?.('product-info') || container;
    const variantJson = productInfo.querySelector?.('variant-selects [data-selected-variant]');

    if (!variantJson?.textContent) return null;

    try {
      return JSON.parse(variantJson.textContent);
    } catch (error) {
      return null;
    }
  },

  containerHasPlaceholder(container) {
    if (!container) return false;
    if (container.innerHTML?.includes('[points_amount]')) return true;
    if (container.textContent?.includes('[points_amount]')) return true;
    return !!container.querySelector?.('[data-ssr-text]');
  },

  decodeSmileText(value) {
    if (!value) return value;

    let text = value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"');

    try {
      text = decodeURIComponent(text);
    } catch (error) {
      /* keep decoded html entities only */
    }

    return text;
  },

  replacePointsPlaceholder(html, points) {
    return html
      .replace(/\[points_amount\]/g, String(points))
      .replace(/%5Bpoints_amount%5D/gi, String(points));
  },

  updatePointsText(container, points) {
    if (points === null || points === undefined) return;

    container.querySelectorAll('[data-ssr-text]').forEach((element) => {
      const decoded = this.decodeSmileText(element.innerHTML);
      if (decoded.includes('[points_amount]')) {
        element.innerHTML = this.replacePointsPlaceholder(decoded, points);
      }
    });

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      if (walker.currentNode.textContent.includes('[points_amount]')) {
        textNodes.push(walker.currentNode);
      }
    }

    textNodes.forEach((node) => {
      node.textContent = node.textContent.replace(/\[points_amount\]/g, String(points));
    });

    container.querySelectorAll('.shopify-app-block').forEach((block) => {
      if (block.innerHTML.includes('[points_amount]')) {
        block.innerHTML = this.replacePointsPlaceholder(block.innerHTML, points);
      }
    });
  },

  sync(container, variant) {
    if (!container || !this.containerHasPlaceholder(container)) return Promise.resolve();

    const resolvedVariant = variant || this.getVariantFromContainer(container);
    if (!resolvedVariant) return Promise.resolve();

    return this.fetchConfig().then((config) => {
      if (!config) return;

      const points = this.calculatePoints(resolvedVariant, config);
      if (points === null) return;

      this.updatePointsText(container, points);
    });
  },
};

function cacheModeQuery(designMode) {
  return designMode ? '&no_cache=true' : '';
}

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data?.variant) return;

    document.querySelectorAll('quick-add-modal product-info').forEach((productInfo) => {
      const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
      if (sectionId !== data.sectionId) return;

      window.SmileProductPoints?.sync(productInfo, data.variant);
    });
  });
}
